import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { getCPDataWithParams } from 'src/clinic/services'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import {
  emailPayoutReportInvoicePdf,
  getPaypoutReportsPdfResponse,
  sendPayoutReportInvoicePdfToCustomRecipient,
  sendPayoutReportInvoicePdfToFinanceAdmins,
  sendPayoutReportInvoicePdfToPractitioner,
  sendPayoutReportInvoicePdfToSupportUser,
} from '../util'

export const fetchReportsData = async ({
  page = 0,
  reportDateMoment,
  reportEndDateMoment,
  dataFetchingApiUrl,
  extraParams,
  exportEventType,
  entityType,
  useLocalDateFormatForApiCalls,
  setLoadingPercentage,
}) => {
  try {
    const isTherapistReport = entityType === 'mentor'

    const params = {
      ...extraParams,
      eventType: extraParams?.eventType || exportEventType,
      page,
      pageSize: 500,
    }

    if (isTherapistReport) {
      params.startTime = reportDateMoment
        .clone()
        .utc()
        .format(
          useLocalDateFormatForApiCalls
            ? MOMENT_FORMATS.javaLocalDateFormatNew
            : MOMENT_FORMATS.javaInstantFormatNew,
        )
      params.endTime = reportEndDateMoment
        .clone()
        .utc()
        .format(
          useLocalDateFormatForApiCalls
            ? MOMENT_FORMATS.javaLocalDateFormatNew
            : MOMENT_FORMATS.javaInstantFormatNew,
        )
    } else {
      params.startDate = reportDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat)
      params.endDate = reportEndDateMoment
        .clone()
        .add(1, 'day')
        .format(MOMENT_FORMATS.javaLocalDateFormat)
    }

    const resp = await getCPDataWithParams(dataFetchingApiUrl, params)

    if (resp.status === 200) {
      setLoadingPercentage((resp?.data?.number + 1) / resp?.data?.totalPages)
      return {
        data: resp.data.content,
        isLastPage: resp.data.last,
      }
    }
  } catch (error) {
    throw new Error('Failed to fetch reports')
  }
}

export const downloadPdfReports = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await getPaypoutReportsPdfResponse(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const emailPdfReports = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  emailToSendTo,
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await emailPayoutReportInvoicePdf(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    emailToSendTo,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const sendPdfReportsToPractitioner = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  deliveryModes,
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await sendPayoutReportInvoicePdfToPractitioner(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    deliveryModes,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const sendPdfReportsToFinanceAdmins = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  deliveryModes = ['mail', 'whatsapp'],
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await sendPayoutReportInvoicePdfToFinanceAdmins(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    deliveryModes,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const sendPdfReportsToCustomRecipient = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  deliveryModes,
  customRecipient,
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await sendPayoutReportInvoicePdfToCustomRecipient(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    deliveryModes,
    customRecipient,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const sendPdfReportsToSupportUser = async ({
  reportDateMoment,
  reportEndDateMoment,
  entityId,
  entityType,
  exportEventType,
  deliveryModes = ['mail', 'whatsapp'],
  sendRazorpayNotifications,
  sendRazorpayReminderNotifications,
  addTdsRelatedInfo,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  billDate = dayjs(),
  includeNonAumhumAffiliations = false,
}) => {
  const reportsPdfResponse = await sendPayoutReportInvoicePdfToSupportUser(
    {
      targetEntityId: entityId,
      targetEntityType: entityType,
      startDate: reportDateMoment.format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment.clone().format(MOMENT_FORMATS.javaLocalDateFormat),
      billDate: billDate.format(MOMENT_FORMATS.javaLocalDateFormat),
    },
    exportEventType,
    deliveryModes,
    sendRazorpayNotifications,
    sendRazorpayReminderNotifications,
    addTdsRelatedInfo,
    blacklistedEventIds,
    includeAllSessionsInReport,
    hideGeneratedOn,
    includeNonAumhumAffiliations,
  )
  return reportsPdfResponse
}

export const collectAllReportsData = async (fetchParams) => {
  const collectedData = []
  for (let page = 0; ; page++) {
    const { data, isLastPage } = await fetchReportsData({
      ...fetchParams,
      page,
    })
    collectedData.push(...data)
    if (isLastPage) break
  }

  if (collectedData.length === 0) {
    toast.info('No data available for the selected date range.')
    return null
  }

  return collectedData
}
